import { Component, Input, OnDestroy } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors } from '@angular/forms';
import { map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { IZbsApp } from '../../../services/Ebootis.ServiceProxies/IZbsApp';

/**
 * Die `ZipCodeInputComponent` ist eine Komponente, die zur Eingabe und Validierung
 * von Postleitzahlen (PLZ) in einem Formular verwendet wird. Diese Komponente unterstützt
 * die dynamische Validierung von PLZs basierend auf dem ausgewählten Land und ermöglicht
 * die automatische Ermittlung und Anzeige der zugehörigen Stadt/Ort. (Länderverwaltung/Postleitzahlen im ERPII)
 * 
 * Beispielnutzung:
 * ```html
 * <app-zip-code-input
 *   formFieldClass="w-100"
 *   [required]="true"
 *   [cityCtrl]="cityFormControl"
 *   [zipCtrl]="zipFormControl"
 *   [countryCtrl]="countryFormControl">
 * </app-zip-code-input>
 * ```
 **/
@Component({
  selector: 'app-zip-code-input',
  templateUrl: './zip-code-input.component.html',
  styleUrls: ['./zip-code-input.component.scss']
})
export class ZipCodeInputComponent implements OnDestroy {

  // Custom Async Validator
  validateZipCode(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      let observable: Observable<any> = of(null);
      if (!control || !this.m_countryCtrl)
        return of(null);
      const zipCode = control.value;
      const country = this.m_countryCtrl.value;
      if (!country || !zipCode)
        return observable;

      if ((country === 'DE' && zipCode.length < 5 && this.m_cityCtrl.value)) {
        this.m_cityCtrl.setValue(null);
        if (this.m_cityCtrl.disabled)
          this.m_cityCtrl.enable({ emitEvent: false });
        this.cities = [];
      }

      // Validierung für Deutschland (mindestens 5 Zeichen), damit bei jedem Zeichen eine PLZ Prüfung bemüht wird.
      // Für andere Länder besitzen wir leider nicht die Informationen wie lang eine PLZ ist bzw. ob es überhaupt eine PLZ gibt (aus sicht des ERPs)
      if ((country === 'DE' && zipCode.length >= 5) || country !== 'DE')
        observable = this.m_service.getCityForZipCode(country, zipCode);
      return observable
        .pipe(take(1))
        .pipe(
          map((res: IZbsApp.CityForZipCodeResult) => {
            this.cities = res?.cities || [];
            if (res) {
              switch (res.retCode) {
                case 2:
                  return { zipNotExist: true };
                case 3:
                  return { countryDefNotExist: true };
                default:
                  // eine eindeutige PLZ gefunden
                  if (res?.cities?.length === 1) {

                    if (this.m_cityCtrl) {
                      this.m_cityCtrl.setValue(res.cities[0]);
                      if (res.cities[0])
                        this.m_cityCtrl.disable();
                      else this.m_cityCtrl.enable({ emitEvent: false });
                    }
                  }
                  return null;
              }
            }
            return null;
          })
        );
    };
  }

  /** Style welcher dem MatFormField zugeordnet wird */
  @Input('formFieldClass') m_formFieldClass: string;
  /** Eingabe einer PLZ ist notwendig? */
  @Input('required') m_required: boolean = true;
  /** FormControl des Ortes (optional, wenn angegeben wird versucht Ort anhand PLZ zu ermitteln) */
  @Input('cityCtrl') m_cityCtrl: FormControl<string>;
  /** FormControl der PLZ */
  @Input('zipCtrl') set zipCtrl(p_ctrl: FormControl<string>) {
    this.m_zipCtrl = p_ctrl;
    this.m_zipCtrl.addAsyncValidators(this.validateZipCode());
    this.m_zipCtrl.updateValueAndValidity({ emitEvent: false });
    this.m_zipCtrl.valueChanges.pipe(takeUntil(this.m_subscriptionsSubject)).subscribe({
      next: (zip) => {
        if (zip === '')
          this.m_cityCtrl.enable({ emitEvent: false });
      }
    });
  }
  /** FormControl des Landes */
  @Input('countryCtrl') m_countryCtrl: FormControl<string>;

  m_zipCtrl: FormControl<string>;

  public cities: string[] = [];

  private readonly m_subscriptionsSubject = new Subject();

  constructor(
    private m_service: IZbsApp,
  ) { }

  ngOnDestroy(): void {
    this.m_subscriptionsSubject.next(null);
    this.m_subscriptionsSubject.complete();
  }
}
